// Analítica de Auditoría. Contabilidad → Libros y Auxiliares.
//
// Las cinco pruebas que el panel llamó «la diferencia disruptiva»: las que hoy
// aporta el auditor externo con ACL o IDEA, exportando el sistema a un archivo y
// trabajando fuera. Que vengan de fábrica cambia quién las corre y cuándo — el
// contador puede pasarlas él mismo antes de que llegue nadie.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BarChart3, Copy, ShieldAlert, Users, Dices, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { field, rpc, usd, hoy } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const AUTH = { headers: { 'Content-Profile': 'autenticacion', 'Accept-Profile': 'autenticacion' } };
const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const rpcAuth = async <T,>(fn: string, b: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, b, AUTH)).data;
const rpcFin = async <T,>(fn: string, b: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, b, FIN)).data;

const inicioAnio = `${new Date().getFullYear()}-01-01`;
const POBLACIONES = [
  { v: 'compras', l: 'Compras' }, { v: 'ventas', l: 'Ventas' },
  { v: 'asientos', l: 'Líneas de asiento' }, { v: 'pagos', l: 'Pagos bancarios' },
];

interface Digito { digito: number; observado: number; frec_obs: number; frec_esp: number; desviacion: number; z: number | null }
interface Benford {
  registros: number; mad: number; conformidad: string; suficiente: boolean;
  digitos: Digito[]; grupos_sospechosos: { grupo: string; registros: number; mad: number }[];
}
interface Dup {
  grupos: { nivel: number; motivo: string; ruc: string | null; proveedor: string;
            importe: number; documentos: number; series: string[]; fechas: string[] }[];
  resumen: { grupos: number; expuesto: number };
}
interface Muestra {
  parametros: Record<string, unknown>; n_seleccionado: number; valor_seleccionado: number;
  seleccion: { id: string; fecha: string; documento: string; tercero: string; usuario: string | null; monto: number }[];
}
interface Sod {
  usuarios: { usuario_id: string; username: string; perfil: string; nivel_acceso: number;
              conflictos: number; altos: number;
              detalle: { clave: string; titulo: string; riesgo: string; porque: string }[] }[];
  por_conflicto: { clave: string; titulo: string; riesgo: string; usuarios: number }[];
  catalogo: number; resumen: { usuarios_con_conflicto: number; conflictos_altos: number }; nota: string;
}
interface Maestro {
  proveedor_es_empleado: { proveedor: string; identificacion: string; empleado: string }[];
  cuenta_de_empleado: { proveedor: string; cuenta: string; empleado: string }[];
  cuenta_compartida: { cuenta: string; proveedores: string[]; cuantos: number }[];
  creado_y_facturado: { proveedor: string; identificacion: string; creado: string; primera_compra: string; importe: number }[];
}

const TABS = [
  { v: 'benford', l: 'Benford', icon: BarChart3 },
  { v: 'duplicados', l: 'Duplicados', icon: Copy },
  { v: 'muestra', l: 'Muestreo', icon: Dices },
  { v: 'sod', l: 'Segregación', icon: ShieldAlert },
  { v: 'maestro', l: 'Maestro', icon: Users },
] as const;

export function AnaliticaAuditoria() {
  const [tab, setTab] = useState<(typeof TABS)[number]['v']>('benford');
  const [desde, setDesde] = useState(inicioAnio);
  const [hasta, setHasta] = useState(hoy);
  const [pob, setPob] = useState('compras');
  const [pos, setPos] = useState(1);
  const [metodo, setMetodo] = useState('aleatorio');
  const [n, setN] = useState(30);
  const [semilla, setSemilla] = useState('');

  const benford = useQuery<Benford>({
    queryKey: ['aud-benford', pob, pos, desde, hasta], enabled: tab === 'benford',
    queryFn: () => rpc<Benford>('aud_benford', { _poblacion: pob, _desde: desde, _hasta: hasta, _pos: pos }),
  });
  const dups = useQuery<Dup>({
    queryKey: ['aud-dups', desde, hasta], enabled: tab === 'duplicados',
    queryFn: () => rpcFin<Dup>('aud_compras_duplicadas', { _desde: desde, _hasta: hasta }),
  });
  const muestra = useQuery<Muestra>({
    queryKey: ['aud-muestra', pob, metodo, n, semilla, desde, hasta], enabled: tab === 'muestra',
    queryFn: () => rpc<Muestra>('aud_muestra', {
      _poblacion: pob, _metodo: metodo, _n: n, _semilla: semilla || null, _desde: desde, _hasta: hasta }),
  });
  const sod = useQuery<Sod>({
    queryKey: ['aud-sod'], enabled: tab === 'sod', queryFn: () => rpcAuth<Sod>('aud_sod', {}),
  });
  const maestro = useQuery<Maestro>({
    queryKey: ['aud-maestro'], enabled: tab === 'maestro',
    queryFn: () => rpcFin<Maestro>('aud_coincidencias_maestro', {}),
  });

  const cargando = [benford, dups, muestra, sod, maestro].some((q) => q.isFetching);
  const b = benford.data;
  const maxFrec = Math.max(...(b?.digitos ?? []).map((d) => Math.max(d.frec_obs, d.frec_esp)), 1);

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <BarChart3 className="w-5 h-5" /> Analítica de Auditoría
      </h1>
      <p className="text-sm text-muted-foreground">
        Las pruebas que un auditor externo corre sobre el <b>100 % de la población</b>, no sobre una muestra.
        Normalmente las aporta él con ACL o IDEA después de exportar los datos; aquí se pasan desde dentro, antes
        de que llegue nadie.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm border ${
              tab === t.v ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
        {cargando && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>

      {tab !== 'sod' && tab !== 'maestro' && (
        <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <label className="flex flex-col text-xs text-muted-foreground">Desde
            <input type="date" className={`${field} w-36 mt-1`} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
          <label className="flex flex-col text-xs text-muted-foreground">Hasta
            <input type="date" className={`${field} w-36 mt-1`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
          {tab !== 'duplicados' && (
            <label className="flex flex-col text-xs text-muted-foreground">Población
              <select className={`${field} w-44 mt-1`} value={pob} onChange={(e) => setPob(e.target.value)}>
                {POBLACIONES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select></label>
          )}
          {tab === 'benford' && (
            <label className="flex flex-col text-xs text-muted-foreground">Dígito
              <select className={`${field} w-40 mt-1`} value={pos} onChange={(e) => setPos(Number(e.target.value))}>
                <option value={1}>Primero</option><option value={2}>Segundo</option>
                <option value={12}>Primeros dos</option>
              </select></label>
          )}
          {tab === 'muestra' && (<>
            <label className="flex flex-col text-xs text-muted-foreground">Método
              <select className={`${field} w-48 mt-1`} value={metodo} onChange={(e) => setMetodo(e.target.value)}>
                <option value="aleatorio">Aleatorio simple</option>
                <option value="mus">Unidad monetaria (MUS)</option>
                <option value="clave">Partidas clave</option>
              </select></label>
            <label className="flex flex-col text-xs text-muted-foreground">n
              <input type="number" className={`${field} w-20 mt-1`} value={n}
                onChange={(e) => setN(Math.max(1, Number(e.target.value) || 1))} /></label>
            <label className="flex flex-col text-xs text-muted-foreground">Semilla
              <input className={`${field} w-40 mt-1`} placeholder="papel-2026" value={semilla}
                onChange={(e) => setSemilla(e.target.value)} /></label>
          </>)}
        </CardContent></Card>
      )}

      {/* ───────────────────────────────── BENFORD ───────────────────────── */}
      {tab === 'benford' && b && (
        <>
          <Card><CardContent className="flex flex-wrap items-center gap-6 pt-4">
            <div><div className="text-xs text-muted-foreground">Registros</div>
              <div className="text-xl font-semibold font-mono tabular-nums">{b.registros?.toLocaleString(LOCALE)}</div></div>
            <div><div className="text-xs text-muted-foreground">MAD</div>
              <div className="text-xl font-semibold font-mono tabular-nums">{Number(b.mad ?? 0).toFixed(4)}</div></div>
            <div><div className="text-xs text-muted-foreground">Lectura</div>
              <div className={`text-lg font-semibold ${
                b.conformidad === 'NO CONFORME' ? 'text-red-600'
                  : b.conformidad === 'conformidad marginal' ? 'text-amber-600'
                  : !b.suficiente ? 'text-muted-foreground' : 'text-green-600'}`}>{b.conformidad}</div></div>
            {!b.suficiente && (
              <p className="text-xs text-amber-600 max-w-sm">
                Benford necesita volumen: por debajo de 300 registros el resultado no significa nada. Amplía el
                período o cambia de población antes de sacar conclusiones.
              </p>
            )}
          </CardContent></Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">
              Frecuencia observada contra la esperada
            </CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Dígito</th><th className="py-2 pr-3 text-right">Casos</th>
                  <th className="py-2 pr-3 text-right">Observado</th><th className="py-2 pr-3 text-right">Esperado</th>
                  <th className="py-2 pr-3">Comparación</th><th className="py-2 pr-3 text-right">Z</th>
                </tr></thead>
                <tbody>
                  {b.digitos.map((d) => {
                    const sospechoso = Number(d.z) > 1.96;
                    return (
                      <tr key={d.digito} className={`border-b last:border-0 ${sospechoso ? 'bg-red-500/5' : ''}`}>
                        <td className="py-1.5 pr-3 font-mono font-semibold">{d.digito}</td>
                        <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{d.observado}</td>
                        <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{Number(d.frec_obs).toFixed(2)}%</td>
                        <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-muted-foreground">{Number(d.frec_esp).toFixed(2)}%</td>
                        <td className="py-1.5 pr-3 w-56">
                          {/* Dos barras superpuestas: la esperada en gris, la observada encima */}
                          <div className="relative h-3 rounded bg-muted/40">
                            <div className="absolute inset-y-0 left-0 rounded bg-muted-foreground/30"
                              style={{ width: `${(d.frec_esp / maxFrec) * 100}%` }} />
                            <div className={`absolute inset-y-0 left-0 rounded ${sospechoso ? 'bg-red-500/70' : 'bg-primary/70'}`}
                              style={{ width: `${(d.frec_obs / maxFrec) * 100}%`, height: '0.45rem', top: '0.175rem' }} />
                          </div>
                        </td>
                        <td className={`py-1.5 pr-3 text-right font-mono tabular-nums ${sospechoso ? 'font-semibold text-red-600' : ''}`}
                          title={sospechoso ? 'Por encima de 1,96: la desviación de este dígito no se explica por azar' : ''}>
                          {d.z == null ? '—' : Number(d.z).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-muted-foreground">
                La barra gris es lo que Benford predice; la de color, lo observado. En rojo, los dígitos con
                Z &gt; 1,96.
              </p>
            </CardContent>
          </Card>

          {(b.grupos_sospechosos?.length ?? 0) > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Dónde mirar</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-2">
                  El MAD del total esconde al que factura siempre empezando por el mismo dígito. Éstos son los que
                  más se desvían, con al menos 50 registros.
                </p>
                <div className="space-y-1">
                  {b.grupos_sospechosos.slice(0, 8).map((g) => (
                    <div key={g.grupo} className="flex items-center gap-3 text-sm">
                      <span className="flex-1 truncate">{g.grupo}</span>
                      <span className="text-xs text-muted-foreground">{g.registros} reg.</span>
                      <span className={`font-mono tabular-nums ${Number(g.mad) > 0.015 ? 'text-red-600 font-semibold' : ''}`}>
                        {Number(g.mad).toFixed(4)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ──────────────────────────────── DUPLICADOS ─────────────────────── */}
      {tab === 'duplicados' && dups.data && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            {dups.data.resumen?.grupos ?? 0} grupo(s) · {usd(dups.data.resumen?.expuesto ?? 0)} expuestos
          </CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {(dups.data.grupos?.length ?? 0) === 0 && !dups.isFetching && (
              <p className="py-8 text-center text-sm text-muted-foreground">Sin coincidencias en el período.</p>
            )}
            {(dups.data.grupos?.length ?? 0) > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Nivel</th><th className="py-2 pr-3">Proveedor</th>
                  <th className="py-2 pr-3 text-right">Importe</th><th className="py-2 pr-3">Series</th>
                  <th className="py-2 pr-3">Fechas</th>
                </tr></thead>
                <tbody>
                  {dups.data.grupos.map((g, i) => (
                    <tr key={i} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-3">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          g.nivel === 1 ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}`}>
                          {g.nivel}
                        </span>
                        <div className="text-xs text-muted-foreground mt-0.5 max-w-56">{g.motivo}</div>
                      </td>
                      <td className="py-2 pr-3">{g.proveedor}
                        {g.ruc && <div className="text-xs font-mono text-muted-foreground">{g.ruc}</div>}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums font-medium">{usd(g.importe)}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{(g.series ?? []).join(', ')}</td>
                      <td className="py-2 pr-3 text-xs">{(g.fechas ?? []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ───────────────────────────────── MUESTREO ──────────────────────── */}
      {tab === 'muestra' && muestra.data && (
        <>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Hoja de parámetros</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-2">
                Esto <b>es</b> el papel de trabajo. La NIA 530 no exige un motor estadístico: exige poder
                documentar y <b>repetir</b> la selección. Con la misma semilla sale siempre la misma muestra.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {Object.entries(muestra.data.parametros ?? {}).map(([k, v]) => (
                  <div key={k} className="rounded-lg border p-2">
                    <div className="text-xs text-muted-foreground">{k.replace(/_/g, ' ')}</div>
                    <div className="font-mono text-sm tabular-nums break-all">{String(v ?? '—')}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">
              Selección · {muestra.data.n_seleccionado} partidas · {usd(muestra.data.valor_seleccionado)}
            </CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Documento</th><th className="py-2 pr-3">Tercero</th>
                  <th className="py-2 pr-3">Registró</th><th className="py-2 pr-3 text-right">Importe</th>
                </tr></thead>
                <tbody>
                  {(muestra.data.seleccion ?? []).map((s, i) => (
                    <tr key={s.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{String(s.fecha).slice(0, 10)}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{s.documento ?? '—'}</td>
                      <td className="py-1.5 pr-3 truncate max-w-xs">{s.tercero ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-xs">{s.usuario ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{usd(s.monto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {/* ──────────────────────────── SEGREGACIÓN ────────────────────────── */}
      {tab === 'sod' && sod.data && (
        <>
          <Card><CardContent className="flex flex-wrap items-center gap-6 pt-4">
            <div><div className="text-xs text-muted-foreground">Usuarios con conflicto</div>
              <div className={`text-xl font-semibold font-mono tabular-nums ${
                (sod.data.resumen?.usuarios_con_conflicto ?? 0) > 0 ? 'text-amber-600' : ''}`}>
                {sod.data.resumen?.usuarios_con_conflicto ?? 0}</div></div>
            <div><div className="text-xs text-muted-foreground">De riesgo alto</div>
              <div className={`text-xl font-semibold font-mono tabular-nums ${
                (sod.data.resumen?.conflictos_altos ?? 0) > 0 ? 'text-red-600' : ''}`}>
                {sod.data.resumen?.conflictos_altos ?? 0}</div></div>
            <div><div className="text-xs text-muted-foreground">Pares vigilados</div>
              <div className="text-xl font-semibold font-mono tabular-nums">{sod.data.catalogo}</div></div>
            <p className="text-xs text-muted-foreground max-w-lg">{sod.data.nota}</p>
          </CardContent></Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Quién acumula funciones incompatibles</CardTitle></CardHeader>
            <CardContent>
              {(sod.data.usuarios?.length ?? 0) === 0 && (
                <p className="py-8 text-center text-sm text-green-600">
                  Ningún usuario acumula funciones incompatibles.
                </p>
              )}
              <div className="space-y-2">
                {(sod.data.usuarios ?? []).map((u) => (
                  <div key={u.usuario_id} className="rounded-lg border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{u.username}</span>
                      <span className="text-xs text-muted-foreground">{u.perfil} · nivel {u.nivel_acceso}</span>
                      <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-semibold ${
                        u.altos > 0 ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}`}>
                        {u.conflictos} conflicto(s){u.altos > 0 ? ` · ${u.altos} alto(s)` : ''}
                      </span>
                    </div>
                    <ul className="mt-1.5 space-y-1">
                      {(u.detalle ?? []).map((d) => (
                        <li key={d.clave} className="text-sm">
                          <span className={d.riesgo === 'alto' ? 'text-red-600' : 'text-amber-600'}>●</span>{' '}
                          {d.titulo}
                          <div className="text-xs text-muted-foreground ml-4 italic">{d.porque}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ─────────────────────────────── MAESTRO ─────────────────────────── */}
      {tab === 'maestro' && maestro.data && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Los cruces del <b>proveedor fantasma</b>. Los datos siempre estuvieron en la base; lo que faltaba era
            que alguien los volviera a mirar el mes que viene.
          </p>
          {[
            { k: 'proveedor_es_empleado', t: 'Proveedor con la identificación de un empleado', alto: true,
              render: (r: Maestro['proveedor_es_empleado'][0]) => `${r.proveedor} (${r.identificacion}) = ${r.empleado}` },
            { k: 'cuenta_de_empleado', t: 'Proveedor que cobra en la cuenta de un empleado', alto: true,
              render: (r: Maestro['cuenta_de_empleado'][0]) => `${r.proveedor} · ${r.cuenta} = ${r.empleado}` },
            { k: 'cuenta_compartida', t: 'Proveedores que comparten cuenta bancaria', alto: true,
              render: (r: Maestro['cuenta_compartida'][0]) => `${r.cuenta}: ${(r.proveedores ?? []).join(' / ')}` },
            { k: 'creado_y_facturado', t: 'Proveedor creado y facturado el mismo día', alto: false,
              render: (r: Maestro['creado_y_facturado'][0]) => `${r.proveedor} · creado ${r.creado} · 1ª compra ${r.primera_compra} · ${usd(r.importe)}` },
          ].map((s) => {
            const filas = (maestro.data as unknown as Record<string, unknown[]>)[s.k] ?? [];
            return (
              <Card key={s.k} className={filas.length > 0 && s.alto ? 'border-red-500/40' : ''}>
                <CardHeader className="pb-2"><CardTitle className="text-base">
                  {s.t} <span className="text-muted-foreground font-normal">· {filas.length}</span>
                </CardTitle></CardHeader>
                <CardContent>
                  {filas.length === 0
                    ? <p className="text-sm text-green-600">Ninguna coincidencia.</p>
                    : <ul className="space-y-1 text-sm">
                        {filas.slice(0, 20).map((r, i) => (
                          <li key={i} className="font-mono text-xs">{s.render(r as never)}</li>
                        ))}
                      </ul>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {tab === 'muestra' && (
        <Button variant="outline" size="sm"
          onClick={() => setSemilla(`papel-${new Date().toISOString().slice(0, 10)}`)}>
          Usar la fecha de hoy como semilla
        </Button>
      )}
    </div>
  );
}
